import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Header = () => {
  const t = useT();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (path) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Desktop nav, the logo carries "Home"; FAQ lives in the footer and mobile menu.
  const navLinks = [
    { path: '/mice', key: 'mice' },
    { path: '/events', key: 'events' },
    { path: '/weddings', key: 'weddings' },
    { path: '/portfolio', key: 'portfolio' },
    { path: '/journal', key: 'journal' },
    { path: '/studio', key: 'studio' },
    { path: '/contact', key: 'contact' }
  ];

  const mobileNavLinks = [{ path: '/', key: 'home' }, ...navLinks.slice(0, 6), { path: '/faq', key: 'faq' }, { path: '/contact', key: 'contact' }];

  // Hide the "Book a Private Consultation" CTA on pages that own a single dedicated CTA pattern.
  const path = location.pathname;
  const hideBookCta =
    path === '/contact' ||
    path === '/studio' ||
    path === '/faq' ||
    path === '/portfolio' ||
    path.startsWith('/portfolio/') ||
    path === '/journal' ||
    path.startsWith('/journal/');

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-bone/95 backdrop-blur-sm border-b border-divider'
          : 'bg-gradient-to-b from-[rgba(6,11,34,0.72)] via-[rgba(6,11,34,0.32)] to-transparent'
      }`}
    >
      <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="GlowMeets home" data-testid="header-logo">
            <img
              src="/glowmeets-mark.png"
              alt="GlowMeets"
              className="h-12 w-auto"
              style={{ display: 'block' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-x-5 xl:gap-x-6">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#D6A87A]'
                    : isScrolled
                      ? 'text-text-primary hover:text-[#D6A87A]'
                      : 'text-bone/90 hover:text-[#D6A87A]'
                }`}
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}

            {/* Language toggle, between nav and CTA pill */}
            <div
              className="flex items-center ps-3"
              style={{ borderInlineStart: '1px solid rgba(214,168,122,0.35)' }}
            >
              <LanguageToggle variant="header" compact onDark={!isScrolled} />
            </div>

            {!hideBookCta && (
              <a
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0CId90EJoI3OIEAkkMicBkzi7eIQzm0oh-ko0CCTnRTqOx95qXQkTO5KO09KRorKK6IY7-8e_C"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  className={`px-5 rounded-full whitespace-nowrap transition-colors duration-300 ${
                    isScrolled
                      ? 'bg-navy hover:bg-copper-deep text-white'
                      : 'bg-[#D6A87A] hover:bg-[#B98556] text-navy'
                  }`}
                >
                  {t('cta.bookConsultation')}
                </Button>
              </a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-text-primary p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-bone/98 backdrop-blur-md border-t border-divider">
          <nav className="px-4 py-6 space-y-4">
            {mobileNavLinks.map((link) => (
              <button
                key={link.path + link.key}
                onClick={() => handleNavClick(link.path)}
                className={`block text-base font-medium tracking-wide transition-colors duration-200 w-full text-left ${
                  location.pathname === link.path
                    ? 'text-[#D6A87A]'
                    : 'text-text-primary hover:text-[#D6A87A]'
                }`}
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}

            <div className="pt-4">
              <LanguageToggle variant="header" />
            </div>

            {!hideBookCta && (
              <a
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0CId90EJoI3OIEAkkMicBkzi7eIQzm0oh-ko0CCTnRTqOx95qXQkTO5KO09KRorKK6IY7-8e_C"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Button className="bg-navy hover:bg-copper-deep text-white w-full mt-4 rounded-full">
                  {t('cta.bookConsultation')}
                </Button>
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
